import { call, put, takeLatest, all, delay, select } from 'redux-saga/effects';
import {
  createUserRequest,
  createUserStart,
  createUserSuccess,
  createUserFailure,
  deleteUserRequest,
  deleteUserStart,
  deleteUserSuccess,
  deleteUserFailure,
  findUserStart,
  findUserSuccess,
  findUserFailure,
  updateUserAttributesRequest,
  updateUserAttributesStart,
  updateUserAttributesSuccess,
  updateUserAttributesFailure,
  changePasswordStart,
  changePasswordSuccess,
  changePasswordFailure,
  changePasswordRequest,
  getUserDataRequest,
} from '../userSlice';
import {
  doesUserExistByEmailApi,
  createIdentityUserApi,
  createUserApi,
  deleteUserApi,
  getUserApi,
  updateUserApi,
  changePasswordApi,
} from '../../api/apiLists/userApi';
import { callWithAuth } from '../sagaApiHelper';
import { showAlert } from '../alertSlice';
import { getUser, resetInitiateUser, setCustomerView, setSearchValue } from '../customerSlice';
import { closeDialog } from '../dialogSlice';
import { hideLoader, showLoader } from '../loaderSlice';
import { setCreateUserButtonLoading } from '../userSlice';

// Create User Saga
function* handleCreateUser(action) {
  try {
    yield put(showLoader());
    yield put(createUserStart());
    const { email, firstName, lastName, localPhoneNo, mobileCountryCode, phoneNumber, address, platform, source, verifyEmail } = action.payload;

    if (verifyEmail) {
      const emailCheck = yield call(
        callWithAuth,
        doesUserExistByEmailApi,
        [verifyEmail, platform],
        {},
        false
      );
      if (!emailCheck?.status) {
        throw new Error(emailCheck?.errMsg || 'Email already exists.');
      }
    }

    const createApi = source === 'customer' ? createIdentityUserApi : createUserApi;
    const { data, status, errMsg } = yield call(
      callWithAuth,
      createApi,
      [email, firstName, lastName, phoneNumber, mobileCountryCode, localPhoneNo, address, platform]
    );

    if (!data) {
      console.error("Error creating user:", data);
      throw new Error(errMsg || "Error occured while creating user.");
    }
    yield put(createUserSuccess({
      user: data,
      status: status,
      keepButtonLoading: source === 'customer',
    }));
    if (source === 'customer') {
      yield delay(3000);
      yield put(setSearchValue(email || phoneNumber));
      yield put(getUser(true));
      yield put(closeDialog('createCustomer'));
    }
    yield put(hideLoader());
    yield put(showAlert({
      message:
        source === "customer"
          ? "Customer created successfully. Search to view their profile."
          : "User created successfully.",
      type: "success",
    }));

  } catch (error) {
    yield put(createUserFailure(error.message));
    yield put(hideLoader());
    yield put(showAlert({
      message: error.message || "Error creating user.",
      type: "error",
    }));
  }
}

function* handleDeleteUser(action) {
  const userId = action.payload;

  try {
    yield put(deleteUserStart());
    yield call(deleteUserApi, userId);
    yield put(deleteUserSuccess(userId));
  } catch (error) {
    yield put(deleteUserFailure(error.message));
  }
}

function* handleUpdateUser(action) {
  try {
    yield put(updateUserAttributesStart());
    yield put(showLoader());

    const { email, firstName, lastName, platform } = action.payload;

    yield call(
      callWithAuth,
      updateUserApi,
      [email, firstName, lastName, platform]
    );

    yield put(updateUserAttributesSuccess({ firstName, lastName }));
    yield put(hideLoader());
    yield put(showAlert({ message: "Profile updated successfully", type: "success" }));
  } catch (error) {
    yield put(updateUserAttributesFailure(error.message));
    yield put(hideLoader());
    yield put(showAlert({ message: "Error updating profile", type: "error" }));
  }
}

function* handleChangePassword({ payload }) {
  try {
    yield put(changePasswordStart());

    const accessToken = yield select((state) => state.auth?.user?.tokens?.accessToken);
    const { status, errMsg } = yield call(
      callWithAuth,
      changePasswordApi,
      [payload.currentPassword, payload.newPassword],
      { 'x-access-token': accessToken },
      false
    );

    if (!status) {
      throw new Error(errMsg || "Failed to change password.");
    }

    yield put(changePasswordSuccess());
    yield put(
      showAlert({ message: "Password changed successfully", type: "success" })
    );
  } catch (err) {
    yield put(changePasswordFailure(err.message));
    yield put(showAlert({ message: err.message, type: "error" }));
  }
}

function* handleGetUserData(action) {
  try {
    yield put(findUserStart());
    yield put(showLoader());
    const { phoneNumber = "", email = "", platform = "app" } = action.payload;
    const { data } = yield call(callWithAuth, getUserApi, [email, phoneNumber, platform]);
    // // console.log(data);
    if (!data?.userId) {
      yield put(setCustomerView('noResults'));
      yield put(showAlert({
        message: "No user found.",
        type: "warning",
      }));
      yield put(resetInitiateUser());
      yield put(hideLoader());
      return;
    }
    yield put(findUserSuccess(data));
    yield put(setCustomerView('found'));
    yield put(resetInitiateUser());
    yield put(hideLoader());
    yield put(setCreateUserButtonLoading(false));
  } catch (error) {
    yield put(findUserFailure(error.message));
    yield put(showAlert({
      message: "Error finding user.",
      type: "error",
    }));
    yield put(resetInitiateUser());
    yield put(hideLoader());
    yield put(setCreateUserButtonLoading(false));
  }
}

export default function* userSaga() {
  yield all([
    takeLatest(createUserRequest.type, handleCreateUser),
    takeLatest(deleteUserRequest.type, handleDeleteUser),
    takeLatest(updateUserAttributesRequest.type, handleUpdateUser),
    takeLatest(changePasswordRequest.type, handleChangePassword),
    takeLatest(getUserDataRequest.type, handleGetUserData),
  ]);
}
